import React from 'react'
import { EstateStaffProfileDTO } from '../../interfaces/staff'
import profileIcon from '../../assets/icons/profile-icon.svg'
import FormInput from '../../components/Input'
import { showToast } from '../../utils/toast'
import { useLocation } from 'react-router-dom'
import MultiSelector from '../../components/MultiSelector'
import DefaultButton from '../../components/Button/Button'
import { useFormik } from 'formik'
import EstateService from '../../api/estate-service'
import useAuth from '../../hooks/useAuth'

const EditStaffs = ({ staffMemberId }: { staffMemberId: string }) => {
  const location = useLocation()
  const { user } = useAuth()

  const [loading, setLoading] = React.useState(true)
  const [staff, setStaff] = React.useState<EstateStaffProfileDTO | null>(null)
  const [permissionsEnabled, setPermissionsEnabled] = React.useState(false)

  const searchParams = new URLSearchParams(location.search)
  const staffId = searchParams.get('staffId')
  const staffParamsId = staffMemberId ?? staffId

  React.useEffect(() => {
    const savedStaffData = localStorage.getItem('staffData')
    if (savedStaffData) {
      const parsedData = JSON.parse(savedStaffData)
      setStaff(parsedData)
      const hasPermissions = !!(parsedData?.permissions && parsedData.permissions.length > 0)
      setPermissionsEnabled(parsedData.isOwner ? hasPermissions : false)
      setLoading(false)
    }
  }, [])

  React.useEffect(() => {
    if (staffParamsId) {
      EstateService.getEstateStaffId(staffParamsId)
        .then((data: EstateStaffProfileDTO) => {
          setStaff(data)
          // Initialize permissionsEnabled based on whether staff has any permissions, but only auto-enable for estate owners
          const hasPermissions = !!(data?.permissions && data.permissions.length > 0)
          setPermissionsEnabled(data.isOwner ? hasPermissions : false)
        })
        .catch((err) => {
          showToast({
            description: err.message,
            status: 'error',
            id: 'get-staff-error',
            title: 'Error fetching staff details',
          })
        })
        .finally(() => {
          setLoading(false)
        })
    }
  }, [staffParamsId])

  // Check if editing should be disabled
  const isEditingDisabled = React.useMemo(() => {
    // Disable if logged-in user is an estate owner
    if (user?.isOwner) return true
    // OR disable if the staff member being edited is an estate owner
    if (staff?.isOwner) return true
    return false
  }, [user?.isOwner, staff?.isOwner])

  // Formik setup to edit permissions only
  const formik = useFormik({
    initialValues: {
      permissions: staff?.permissions || [],
    },

    enableReinitialize: true,

    onSubmit: (values) => {
      if (loading) return

      // Prevent submission if editing is disabled
      if (isEditingDisabled) {
        showToast({
          title: 'Permission Denied',
          description: 'Cannot modify permissions for estate owners',
          id: 'owner-edit-error',
          status: 'error',
        })
        return
      }

      setLoading(true)
      EstateService.editEstateStaffId(staffParamsId!, { permissions: values.permissions })
        .then(async () => {
          showToast({
            title: 'Staff permission updated successfully',
            description: 'Please check staff email for further instruction',
            id: 'edit-staff-success',
            status: 'success',
          })

          try {
            const data = await EstateService.getEstateStaffId(staffParamsId)
            setStaff(data)
            const hasPermissions = !!(data?.permissions && data.permissions.length > 0)
            setPermissionsEnabled(data.isOwner ? hasPermissions : false)
          } catch (err: any) {
            showToast({
              description: err.message,
              status: 'error',
              id: 'get-staff-error',
              title: 'Error fetching staff details',
            })
          } finally {
            setLoading(false)
          }
        })
        .catch((err) => {
          setLoading(false)
          showToast({
            title: 'An error occurred while editing the staff',
            description: err.message,
            id: 'edit-staff-error',
            status: 'error',
          })
        })
    },
  })

  // Handle toggle change
  const handleToggleChange = () => {
    if (isEditingDisabled) {
      showToast({
        title: 'Permission Denied',
        description: 'Cannot modify permissions for estate owners',
        id: 'owner-toggle-error',
        status: 'error',
      })
      return
    }
    setPermissionsEnabled(!permissionsEnabled)
    
    // If disabling permissions, clear the selected permissions
    if (permissionsEnabled) {
      formik.setFieldValue('permissions', [])
    }
  }

  // Get the disabled reason for display
  const getDisabledReason = () => {
    if (user?.isOwner) return "Estate owners cannot modify permissions"
    if (staff?.isOwner) return "Cannot modify permissions for estate owners"
    return ""
  }

  return (
    <div className="sm:mt-12 grid gap-y-10">
      <form onSubmit={formik.handleSubmit}>
        <h5 className="font-semibold text-lg mb-5">Staff details</h5>
        <div className="flex bg-white py-5 gap-5 mb-5 sm:mb-10 items-center flex-col sm:flex-row sm:gap-10">
          <div>
            <h5 className="font-medium mb-3">Staff image</h5>
            <div className="bg-[#E9ECEF] h-[170px] w-[160px] flex items-center rounded-lg justify-center">
              <img
                src={staff?.staff.imageUrl ?? profileIcon}
                alt="Staff profile"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 gap-3 w-full sm:grid-cols-2 sm:gap-6">
            <div className="flex flex-col gap-1">
              <h3 className="font-normal text-base text-[#6C757D]">Email Address</h3>
              <FormInput
                value={staff?.staff.email ?? 'N/A'}
                name="email"
                size="md"
                placeholder="Enter staff's email"
                readonly
              />
            </div>

            <div className="flex flex-col gap-1">
              <h3 className="font-normal text-base text-[#6C757D]">Address</h3>
              <FormInput
                value={staff?.staff.address ?? 'N/A'}
                name="address"
                size="md"
                placeholder="Enter staff's address"
                readonly
              />
            </div>

            <div className="flex flex-col gap-1">
              <h3 className="font-normal text-base text-[#6C757D]">State/Country</h3>
              <FormInput
                value={staff?.staff.country ?? 'N/A'}
                name="country"
                size="md"
                placeholder="Enter staff's state/country"
                readonly
              />
            </div>

            <div className="flex flex-col gap-1">
              <h3 className="font-normal text-base text-[#6C757D]">Phone number</h3>
              <FormInput
                value={staff?.staff.phone ?? 'N/A'}
                name="phone"
                size="md"
                placeholder="Enter staff's phone number"
                readonly
              />
            </div>
          </div>
        </div>

        <div className="mb-5">
          <div className="flex justify-end">
            <label className={`flex items-center ${isEditingDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
              {/* The actual checkbox */}
              <input
                type="checkbox"
                className="sr-only"
                checked={permissionsEnabled}
                onChange={handleToggleChange}
                disabled={isEditingDisabled}
              />
              {/* Custom slider */}
              <div
                className={`relative w-10 h-6 transition duration-200 ease-linear rounded-full ${
                  permissionsEnabled ? 'bg-confirmed' : 'bg-gray-300'
                } ${isEditingDisabled ? 'opacity-50' : ''}`}
              >
                <span
                  className={`absolute left-0.5 top-0.5 bg-white w-5 h-5 rounded-full transition-transform transform ${
                    permissionsEnabled ? 'translate-x-4' : 'translate-x-0'
                  } ${isEditingDisabled ? 'opacity-50' : ''}`}
                ></span>
              </div>
              {isEditingDisabled && (
                <span className="ml-2 text-sm text-gray-500">
                  {getDisabledReason()}
                </span>
              )}
            </label>
          </div>
          <MultiSelector
            setSelectedValues={(values: string[]) => {
              if (!isEditingDisabled) {
                formik.setFieldValue('permissions', values)
              }
            }}
            selectedValues={formik.values.permissions}
            options={[
              {
                description: 'This permission allows users to have administrative rights',
                name: 'Admin',
                value: 'admin',
              },
              {
                description: 'This permission allows users to manage residents',
                name: 'Manage Residents',
                value: 'manage_res',
              },
              {
                description: 'This permission allows users to manage staffs',
                name: 'Manage Staffs',
                value: 'manage_staffs',
              },
              {
                description: 'This permission allows users to manage security',
                name: 'Manage Security',
                value: 'manage_security',
              },
            ]}
            label="Staff permissions"
            disabled={!permissionsEnabled || isEditingDisabled}
          />
        </div>
        <div className="flex justify-end gap-5 sm:mt-20 mt-10">
          <DefaultButton
            text="Save"
            className="sm:w-52 grow sm:grow-0 hover:grow-[9] hover:sm:grow-0"
            isLoading={loading}
            loadingText="Saving..."
            type="submit"
            disabled={loading || isEditingDisabled}
            title={isEditingDisabled ? getDisabledReason() : ""}
          />
        </div>
      </form>
    </div>
  )
}

export default EditStaffs
